<template>
  <div class="flex h-full overflow-y-hidden">
    <div class="w-1/4 min-w-64 overflow-visible">
      <app-header-bot-control :bot-id="botId" @change-bot="changeBot"/>
      <list-section :items="templateList"
                    :bot-id="botId"
                    :loaders="loaders"
                    :selected-id="templateId"
                    :add-tooltip-text="isNewTemplate ? 'Cancel Block' : 'Create Block'"
                    :route-props="{ itemIdName: 'blockId', route: ROUTE_NAMES.blocks, name: ROUTE_NAMES.blocks }"
                    :menu-builder="menuBuilder"
                    :renamed-id="renamedId"
                    @add="createNewTemplate"
                    @cancel="cancelNewTemplate"
                    @set-rename-mode="handleRenameMode"
                    @rename="saveTemplateName"
                    @item-selected="selectTemplate"
                    @request-delete="handleRequestDelete"
                    @show="showTemplateUsages"
      >
        <template #empty>
          <img :src="puzzleImage" alt="puzzle image">
          <span>
            Use Blocks if you want to reuse conversations (or their parts) across multiple
            <router-link :to="{ name: ROUTE_NAMES.answers }" target="_blank" class="hover:underline">Answers</router-link> or
            <router-link :to="{ name: ROUTE_NAMES.events }" target="_blank" class="hover:underline">Events</router-link>.
          </span>
        </template>

        <template #delete-item-icon>
          <delete-block-icon class="h-8 w-8 text-gray-dark"/>
        </template>
      </list-section>
    </div>

    <shadowed-wrapper class="w-3/4">
      <div v-if="isEmpty(templateList)" class="flex items-center h-full w-full justify-center">
        <img :src="noBlocksImage" alt="no blocks image">
      </div>

      <template v-else>
        <app-header class="flex justify-between pl-10 pr-5 w-full">
          <app-header-item-control v-if="template"
                                 :item="template"
                                 :is-rename-mode="isRenameModeInAppHeader"
                                 :is-loading="loaders.name"
                                 :menu-builder="menuBuilder"
                                 @set-rename-mode="handleRenameMode"
                                 @rename="saveTemplateName"
                                 @request-delete="handleRequestDelete"
                                 @show="showTemplateUsages"
          >
            <template #multi-bot-item-selector>
              <MultiBotItemSelector v-if="template.multiBotItemId && templateVariantList.length"
                :items="templateVariantList"
                :selected-id="templateId"
                editor-type="Block"
                @item-selected="selectBlockVariant"
                class="ml-4"
              />
            </template>
          </app-header-item-control>

          <template-save-button v-if="template"
            :bot-id="botId"
            :is-disabled="!isPrimaryVariant"
            class="my-auto ml-2"
            @process-new-template="processNewTemplate"
          />
        </app-header>

        <div class="flex w-full h-full">
          <with-loader id="chat-preview-loader"
                       class="border-transparent bg-white w-7/12 min-w-136 h-full"
                       :loading="!template || loaders.conversationJsons"
                       trigger-loader-on-mount
          >
            <chat-preview class="flex flex-grow"
              :item-id="templateId"
              :is-read-only="!isPrimaryVariant"
            />
          </with-loader>

          <with-loader id="editor-sidebar-loader"
                       class="border-transparent bg-white min-w-104 w-5/12 h-full"
                       :loading="!template || loaders.conversationJsons"
                       trigger-loader-on-mount
          >
            <editor-sidebar :bot-id="botId"
              :item="template"
              :is-template-variant="!isPrimaryVariant"
            />
          </with-loader>
        </div>
      </template>
    </shadowed-wrapper>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="itemToDelete"
                   :delete-title="deleteTitle"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="deleteConfirmText"
                   @cancel="cancelDelete"
                   @delete="deleteTemplate"
    >
      <template #delete-item-icon>
        <delete-block-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader'
import AppHeaderItemControl from '@/components/common/AppHeaderItemControl'
import ChatPreview from '@/components/preview/ChatPreview'
import DeleteBlockIcon from '@/assets/images/icons/DeleteBlockIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import EditorSidebar from '@/components/editor/EditorSidebar'
import ListSection from '@/components/common/ListSection.vue'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper'
import SideListMenu from '@/components/common/SideListMenu'
import TemplateSaveButton from './TemplateSaveButton'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import templateService from '@/services/amio/template.service'
import { ROUTE_NAMES } from '@/router'
import { CONVERSATION_TYPES } from '@/store/constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { ITEM_IDS, ITEM_NAMES, MENU_ITEMS } from '@/utils/editor.constants'
import { DELETE_TEXT } from '@/utils/text.constants'
import toaster from '@/utils/toaster'
import findIndex from 'ramda/src/findIndex'
import propEq from 'ramda/src/propEq'
import puzzleImage from '@/assets/images/puzzle.svg'
import noBlocksImage from '@/assets/images/no_blocks.svg'
import sort from 'ramda/src/sort'
import prepend from 'ramda/src/prepend'
import tail from 'ramda/src/tail'
import update from 'ramda/src/update'
import WithLoader from '@/components/common/WithLoader.vue'
import botModule from '@/store/modules/bot.module'
import emailConfigService from '@/services/amio/email-config.service'
import editorUtils from '@/utils/editor.utils'
import stateTreeUtils from '@/utils/state-tree.utils'
import channelModule from '@/store/modules/channel.module'
import errorUtils from '@/utils/error.utils'
import variablesModule from '@/store/modules/variables.module'
import MultiBotItemSelector from '@/components/common/MultiBotItemSelector.vue'

export default {
  name: 'Template',

  metaInfo() {
    const blockName = this.template?.name || ''
    return {
      title: [blockName, 'Blocks'].join(' | ')
    }
  },

  components: {
    // to prevent 'Unknown custom element: <app-header-controls> - did you register the component correctly?', causing
    // component not being imported -> rendered. The source of an error is unknown.
    AppHeaderBotControl: () => import('@/components/common/AppHeaderBotControl.vue'),
    AppHeaderItemControl,
    AppHeader,
    ChatPreview,
    DeleteBlockIcon,
    DeleteDialog,
    EditorSidebar,
    ListSection,
    ShadowedWrapper,
    SideListMenu,
    TemplateSaveButton,
    WithLoader,
    MultiBotItemSelector
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    botId: {
      async handler(botId, previousBotId) {
        if (this.$route.params.blockId === ITEM_IDS.NEW) return

        const currentTemplateMultiBotItemId = this.template?.multiBotItemId
        await botModule.whenBotsReady()
        await this.loadTemplateList(botId)
        if (isEmpty(this.templateList)) return

        const isVariantSwitch = botModule.getPrimaryBotId(botId) === botModule.getPrimaryBotId(previousBotId)
        const blockIdFromVariant = isVariantSwitch && currentTemplateMultiBotItemId ?
          this.templateList.find(template => template.multiBotItemId === currentTemplateMultiBotItemId)?.id :
          null
        const blockId = blockIdFromVariant || head(this.templateList).id
        await this.initTemplate(blockId, botId)
      }
    },
    '$route.params.blockId': {
      async handler(blockId) {
        if (!blockId || blockId === this.templateId) return
        if (blockId === ITEM_IDS.NEW) return
        if (isEmpty(this.templateList)) return

        await botModule.whenBotsReady()
        await this.initTemplate(blockId, this.$route.params.botId)
      }
    }
  },

  data() {
    return {
      isDeleteDialogShown: false,
      renamedId: null,
      isRenameModeInAppHeader: false,
      itemToDelete: null,
      loaders: {
        conversationJsons: false,
        name: false,
        items: true
      },
      templateId: this.$route.params.blockId,
      ROUTE_NAMES,
      puzzleImage,
      noBlocksImage,
      ITEM_IDS
    }
  },

  computed: {
    isSaved: previewModule.isSaved,
    template: templateModule.template,
    templateList: templateModule.templateList,
    templateVariantList: templateModule.templateVariantList,
    botEmailConfig: previewModule.botEmailConfig,
    stateTree: chatBubbleModule.stateTree,
    conversationType: conversationModule.getType,

    deleteTitle() {
      return DELETE_TEXT[this.conversationType]?.title || ''
    },

    deleteDescription() {
      return DELETE_TEXT[this.conversationType]?.getDescription(this.itemToDelete?.name) || ''
    },

    deleteConfirmText() {
      return DELETE_TEXT[this.conversationType]?.confirm || ''
    },

    isPrimaryVariant() {
      return botModule.isPrimaryVariant(this.botId)
    },

    isTemplateIdLoaded() {
      return this.templateList.some(({ id }) => id === this.$route.params.blockId)
    },

    isNewTemplate() {
      return this.template?.id === ITEM_IDS.NEW
    }
  },

  methods: {
    isEmpty,

    menuBuilder() {
      return [
        MENU_ITEMS.rename,
        MENU_ITEMS.show,
        MENU_ITEMS.delete
      ]
    },

    changeBot(bot) {
      this.updateRouteBlockId(this.templateId, bot.id)
      if (!this.isSaved) return

      botModule.setBot(bot)
      channelModule.setChannelList(bot.id)
    },

    updateRouteBlockId(blockId, botId = this.$route.params.botId || this.botId) {
      editorUtils.updateRouteByItemId(ITEM_NAMES.BLOCK, { botId, blockId })
    },

    resolveBlockIdFromRoute() {
      const routeBlockId = this.$route.params.blockId
      const routeMatch = this.templateList.find(template => template.id === routeBlockId)
      if (routeMatch) return routeMatch.id

      return this.template?.id || head(this.templateList).id
    },

    async selectBlockVariant(blockId, botId) {
      this.updateRouteBlockId(blockId, botId)
      await this.initTemplate(blockId, botId)
    },

    async initTemplate(blockId, botId = this.$route.params.botId || this.botId) {
      const template = await templateService.get(botId, blockId)
      await this.processTemplate(template)
      this.updateRouteBlockId(blockId, botId)
    },

    async showTemplateUsages({ id }) {
      if (this.template.id !== id) {
        await this.selectTemplate(id)
        this.updateRouteBlockId(id)
      }

      templateModule.showUsages()
    },

    async loadTemplateList(botId) {
      this.loaders.items = true
      const templates = sort((a, b) => a.name.localeCompare(b.name), await templateService.list(botId))
      templateModule.setTemplateList(templates)
      this.loaders.items = false
    },

    async createNewTemplate() {
      const primaryBotId = botModule.getPrimaryBotId(this.botId)
      const needsMarketSwitch = primaryBotId !== this.botId

      this.updateRouteBlockId(ITEM_IDS.NEW, primaryBotId)
      if (!this.isSaved) return

      if (needsMarketSwitch) {
        await this.loadTemplateList(primaryBotId)
      }

      const newTemplate = { botId: primaryBotId, name: 'New Block', id: ITEM_IDS.NEW }
      await this.processTemplate(newTemplate)
      newTemplate.conversationJsons = conversationModule.getJsons()
      templateModule.setTemplateList(prepend(newTemplate, this.templateList))
      this.renamedId = ITEM_IDS.NEW
      stateTreeUtils.selectNode(this.stateTree)
    },

    cancelNewTemplate() {
      if (!this.isSaved) return

      templateModule.setTemplateList(tail(this.templateList))
      const templateId = head(this.templateList).id
      this.selectTemplate(templateId)
    },

    cancelDelete() {
      this.itemToDelete = null
      this.isDeleteDialogShown = false
    },

    handleRequestDelete(item) {
      this.itemToDelete = item
      this.isDeleteDialogShown = true
    },

    async deleteTemplate(template) {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await templateService.delete(this.botId, template.id)
        const newTemplateId = editorUtils.resolveNewItemId(template.id, this.templateList)
        templateModule.setTemplateList(this.templateList.filter(({ id }) => id !== template.id))

        if (newTemplateId) {
          this.templateId = newTemplateId
          await this.selectTemplate(this.templateId)
        } else {
          await this.$router.replace({ name: ROUTE_NAMES.blocks, params: { botId: this.botId } })
        }

        toaster.success(`Block "${template.name}" has been deleted.`)
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription)
        if (errorDescription.toString().includes('used')) {
          await this.showTemplateUsages(template)
        }
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.itemToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async processNewTemplate(template) {
      templateModule.setTemplateList(update(0, template, this.templateList))
      await this.processTemplate(template)
    },

    async processTemplate(template) {
      this.loaders.conversationJsons = true
      try {
        conversationModule.setArchivedConversationJson(template.conversationJson)
        conversationModule.init(CONVERSATION_TYPES.TEMPLATES, template.id, template.conversationJson, template.conversationJsonHash)
        if (template.conversationJson && this.botId) {
          const {
            answers,
            events,
            answersHashes,
            eventsHashes
          } = await templateService.getReferencedConversations(this.botId, template.id)
          await Promise.all([
            conversationModule.setAnswers(answers),
            conversationModule.setAnswersHashes(answersHashes),
            conversationModule.setEvents(events),
            conversationModule.setEventsHashes(eventsHashes)
          ])
        }
        this.templateId = template.id
        this.template = template

        const templateVariantList = template.multiBotItemId ?
          await templateService.listTemplateVariants(botModule.getPrimaryBotId(this.botId), template.multiBotItemId) :
          []
        templateModule.setTemplateVariantList(templateVariantList)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.conversationJsons = false
      }
    },

    async selectTemplate(templateId) {
      if (this.loaders.conversationJsons || this.renamedId === templateId) return
      if (!this.isSaved || this.template.id === templateId) return

      this.isRenameModeInAppHeader = false
      const template = await templateService.get(this.botId, templateId)
      await this.processTemplate(template)
    },

    async saveTemplateName(updatedTemplate) {
      try {
        this.loaders.name = true
        const index = findIndex(propEq('id', updatedTemplate.id))(this.templateList)
        if (this.templateList[index].name === updatedTemplate.name) return

        templateModule.setTemplateList(update(index, updatedTemplate, this.templateList))
        if (this.template.id === updatedTemplate.id || this.isNewTemplate) {
          this.template = updatedTemplate
        }
        if (this.isNewTemplate) return

        await templateService.update(this.botId, updatedTemplate.id, { name: updatedTemplate.name })
      } catch (err) {
        toaster.error(err)
      } finally {
        this.resetRenameMode()
        this.loaders.name = false
      }
    },

    async initEmailTemplate() {
      if (!botModule.isEmailBot()) return

      this.botEmailConfig = await emailConfigService.get(this.botId)
    },

    handleRenameMode(e) {
      if (e.fromAppHeader) {
        this.isRenameModeInAppHeader = e.mode
        return
      }

      this.renamedId = e.id
    },

    resetRenameMode() {
      this.renamedId = null
      this.isRenameModeInAppHeader = false
    },

    confirmRouteChange(from) {
      if (isEmpty(this.templateList)) return true

      const wasPreviewChanged = editorUtils.detectPreviewChange(this.isNewTemplate)
      // confirm() returns true or false based on which button the user has pressed
      if (wasPreviewChanged) {
        const userConfirmation = confirm(editorUtils.resolveConfirmText(this.isNewTemplate, 'block'))
        if (userConfirmation && from.params.blockId === ITEM_IDS.NEW) {
          templateModule.setTemplateList(tail(this.templateList))
        }

        this.isSaved = userConfirmation
        return userConfirmation
      } else {
        this.isSaved = true
        return true
      }
    }
  },

  async mounted() {
    window.addEventListener('beforeunload', e => editorUtils.onBeforeUnload(e, this.isNewTemplate))

    await botModule.whenBotsReady()

    await Promise.all([
      variablesModule.fetchVariables(),
      this.loadTemplateList(this.botId)
    ])
    if (isEmpty(this.templateList)) return

    const templateId = this.resolveBlockIdFromRoute()
    await this.initTemplate(templateId, this.botId)

    if (this.$route.query.sidebar === 'usages') templateModule.showUsages()

    await this.initEmailTemplate()
  },

  destroyed() {
    window.removeEventListener('beforeunload', e => editorUtils.onBeforeUnload(e, this.isNewTemplate))
    this.template = null
    conversationModule.reset()
    conversationModule.resetArchivedConversationJson()
  },

  beforeRouteUpdate(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  },

  beforeRouteLeave(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  }
}
</script>
